import {
  createContext,
  PropsWithChildren,
  UIEvent,
  useMemo,
  useRef,
} from "react";
import { FloatingButton } from "@/components/FloatingButton/FloatingButton";
import { useTrackScrollDepth } from "@/hooks/useTrackScrollDepth/useTrackScrollDepth";
import { ActiveUtterancesContext } from "./ActiveUtterancesContext";
import { useActiveUtterances } from "./hooks/useActiveUtterances";
import { useAutoScroll } from "./hooks/useAutoScrolling";
import {
  IntersectionMap,
  IntersectUtterance,
  ScrollManagerContextType,
} from "./ScrollManagerContext.decl";
import * as S from "./ScrollManagerProvider.styled";

export const ScrollManagerContext = createContext<ScrollManagerContextType>(
  {} as ScrollManagerContextType
);

type ScrollManagerProviderProps = {
  mobile?: boolean;
};

export function ScrollManagerProvider({
  children,
  mobile,
}: PropsWithChildren<ScrollManagerProviderProps>) {
  const { onScroll: onScrollTrackDepth } = useTrackScrollDepth();

  const intersectionMap = useRef<IntersectionMap>(
    new Map<number, IntersectUtterance>()
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollContext = useMemo(() => ({ intersectionMap }), [intersectionMap]);

  const actives = useActiveUtterances();

  const { handleOnWheel } = useAutoScroll(intersectionMap, actives);

  const handleOnScroll = (e: UIEvent<HTMLDivElement>) => {
    handleOnWheel();
    onScrollTrackDepth(e);
  };

  return (
    <S.Scrollbar
      ref={scrollRef}
      onScroll={handleOnScroll}
      data-test="transcription-container"
      mobile={Boolean(mobile)}
    >
      <ScrollManagerContext.Provider value={scrollContext}>
        <ActiveUtterancesContext.Provider value={actives}>
          <FloatingButton />
          {children}
        </ActiveUtterancesContext.Provider>
      </ScrollManagerContext.Provider>
    </S.Scrollbar>
  );
}
